import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Ticket, Clock, Users, ChevronRight, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/** Format "$25.00" from cents */
const fmtPrice = (cents) => `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;

/** Countdown hook */
const useCountdown = (targetISO) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!targetISO) return null;
  const diff = new Date(targetISO).getTime() - now;
  if (diff <= 0) return { expired: true, d: 0, h: 0, m: 0, s: 0 };
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { expired: false, d, h, m, s };
};

/** Single countdown digit block */
const CountdownBlock = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-lg bg-black/60 border border-amber-400/20 flex items-center justify-center backdrop-blur-sm">
      <span className="text-amber-300 text-lg sm:text-xl md:text-2xl font-bold font-mono tabular-nums">
        {String(value).padStart(2, '0')}
      </span>
    </div>
    <span className="text-white/30 text-[8px] sm:text-[9px] mt-1 sm:mt-1.5 uppercase tracking-widest">{label}</span>
  </div>
);

/** Marquee light dots around the FULL perimeter of the card */
const MarqueeLights = () => {
  const dot = (i) => ({
    backgroundColor: i % 3 === 0 ? '#fbbf24' : i % 3 === 1 ? '#f59e0b' : '#d97706',
    animation: `marquee-pulse ${1 + (i % 7) * 0.3}s ease-in-out infinite`,
    animationDelay: `${(i % 11) * 0.18}s`,
  });

  const topCount = 48;
  const sideCount = 28;
  const bottomCount = 48;

  return (
    <>
      {/* Top edge */}
      <div className="absolute top-0 left-4 right-4 h-2 flex items-center justify-between z-20 pointer-events-none">
        {Array.from({ length: topCount }).map((_, i) => (
          <div key={`t${i}`} className="w-1.5 h-1.5 rounded-full opacity-60 flex-shrink-0" style={dot(i)} />
        ))}
      </div>
      {/* Bottom edge */}
      <div className="absolute bottom-0 left-4 right-4 h-2 flex items-center justify-between z-20 pointer-events-none">
        {Array.from({ length: bottomCount }).map((_, i) => (
          <div key={`b${i}`} className="w-1.5 h-1.5 rounded-full opacity-60 flex-shrink-0" style={dot(i + topCount)} />
        ))}
      </div>
      {/* Left edge */}
      <div className="absolute top-4 bottom-4 left-0 w-2 flex flex-col items-center justify-between z-20 pointer-events-none">
        {Array.from({ length: sideCount }).map((_, i) => (
          <div key={`l${i}`} className="w-1.5 h-1.5 rounded-full opacity-60 flex-shrink-0" style={dot(i + topCount + bottomCount)} />
        ))}
      </div>
      {/* Right edge */}
      <div className="absolute top-4 bottom-4 right-0 w-2 flex flex-col items-center justify-between z-20 pointer-events-none">
        {Array.from({ length: sideCount }).map((_, i) => (
          <div key={`r${i}`} className="w-1.5 h-1.5 rounded-full opacity-60 flex-shrink-0" style={dot(i + topCount + bottomCount + sideCount)} />
        ))}
      </div>
    </>
  );
};

const VIPMarquee = () => {
  const [events, setEvents] = useState([]);
  const [checkingOut, setCheckingOut] = useState(null);
  const [waitlistForm, setWaitlistForm] = useState(null);
  const [waitlistData, setWaitlistData] = useState({ name: '', contact: '' });
  const [waitlistMsg, setWaitlistMsg] = useState('');

  // Poll events
  useEffect(() => {
    const load = () => {
      fetch(`${API}/api/vip-events/active`)
        .then(r => r.json())
        .then(d => setEvents(Array.isArray(d) ? d : []))
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const handleCheckout = async (eventId, tierId) => {
    setCheckingOut(`${eventId}-${tierId}`);
    try {
      const res = await fetch(`${API}/api/vip-events/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_id: eventId, tier_id: tierId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Checkout failed');
        return;
      }
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch {
      alert('Could not start checkout — please try again');
    } finally {
      setCheckingOut(null);
    }
  };

  const handleWaitlist = async (eventId) => {
    if (!waitlistData.name.trim() || !waitlistData.contact.trim()) {
      setWaitlistMsg('Please enter your name and email/phone');
      return;
    }
    try {
      const res = await fetch(`${API}/api/vip-events/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_id: eventId, ...waitlistData }),
      });
      const data = await res.json();
      setWaitlistMsg(data.message || 'Added to waitlist');
      setWaitlistData({ name: '', contact: '' });
      setTimeout(() => { setWaitlistForm(null); setWaitlistMsg(''); }, 3000);
    } catch {
      setWaitlistMsg('Could not join waitlist — try again');
    }
  };

  if (!events.length) return null;

  return (
    <section className="px-4 pb-12" data-testid="vip-marquee-section">
      <AnimatePresence>
        {events.map(event => (
          <VIPEventCard
            key={event.id}
            event={event}
            checkingOut={checkingOut}
            onCheckout={handleCheckout}
            waitlistForm={waitlistForm}
            setWaitlistForm={setWaitlistForm}
            waitlistData={waitlistData}
            setWaitlistData={setWaitlistData}
            waitlistMsg={waitlistMsg}
            onWaitlist={handleWaitlist}
          />
        ))}
      </AnimatePresence>
    </section>
  );
};

const VIPEventCard = ({
  event, checkingOut, onCheckout,
  waitlistForm, setWaitlistForm, waitlistData, setWaitlistData, waitlistMsg, onWaitlist,
}) => {
  const countdown = useCountdown(event.event_date);
  const isLive = event.status === 'live';
  const isSoldOut = event.status === 'sold_out';
  const isOnSale = event.status === 'on_sale';
  const isEnded = event.status === 'ended' || event.status === 'cancelled';
  const isScheduled = event.status === 'scheduled';
  const [showTiers, setShowTiers] = useState(false);

  const totalRemaining = useMemo(() => {
    return event.tiers?.reduce((sum, t) => {
      if (t.total_inventory === -1) return Infinity;
      return sum + (t.remaining || 0);
    }, 0) || 0;
  }, [event.tiers]);

  const lowestPrice = useMemo(() => {
    const prices = (event.tiers || []).filter(t => t.price_cents > 0).map(t => t.price_cents);
    return prices.length ? Math.min(...prices) : 0;
  }, [event.tiers]);

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  // Single-tier: go directly to checkout. Multi-tier: show selector.
  const handleCTA = () => {
    if (!isOnSale && !isLive) return;
    const available = (event.tiers || []).filter(t => t.total_inventory === -1 || t.remaining > 0);
    if (available.length === 1) {
      onCheckout(event.id, available[0].tier_id);
    } else {
      setShowTiers(true);
    }
  };

  // Marquee-wide click handler: open tiers if clickable area
  const handleCardClick = (e) => {
    // Don't trigger on interactive elements (buttons, inputs, links)
    if (e.target.closest('button, a, input')) return;
    if (isOnSale || isLive) handleCTA();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="max-w-4xl mx-auto mb-8"
      data-testid={`vip-event-${event.id}`}
    >
      <div
        className={`relative rounded-2xl overflow-hidden border border-amber-400/15 bg-gradient-to-br from-[#1a1206] via-[#0d0d0d] to-[#0a0a14] ${(isOnSale || isLive) ? 'cursor-pointer' : ''}`}
        onClick={handleCardClick}
        role={(isOnSale || isLive) ? 'button' : undefined}
        tabIndex={(isOnSale || isLive) ? 0 : undefined}
        data-testid="vip-card-clickable"
      >
        <MarqueeLights />

        {/* Glow effects */}
        <div className="absolute top-0 left-1/4 w-[300px] h-[200px] bg-amber-400/5 rounded-full blur-[80px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[200px] h-[200px] bg-amber-600/5 rounded-full blur-[60px] pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row">
          {/* LEFT SIDE — Event Info (~55-60%) */}
          <div className={`flex-1 min-w-0 p-5 sm:p-6 md:p-8 ${event.image_url ? 'md:pr-4' : ''}`}>
            {/* Status Badge */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                {isLive && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/20 border border-red-500/30" data-testid="vip-live-badge">
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-red-400 text-[10px] font-bold tracking-widest uppercase">Live Now</span>
                  </span>
                )}
                {isSoldOut && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10" data-testid="vip-soldout-badge">
                    <span className="text-white/40 text-[10px] font-bold tracking-widest uppercase">Sold Out</span>
                  </span>
                )}
                {isOnSale && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-400/10 border border-amber-400/20" data-testid="vip-onsale-badge">
                    <Ticket size={11} className="text-amber-400" />
                    <span className="text-amber-400 text-[10px] font-bold tracking-widest uppercase">On Sale</span>
                  </span>
                )}
                {event.status === 'scheduled' && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-400/10 border border-indigo-400/20">
                    <Clock size={11} className="text-indigo-400" />
                    <span className="text-indigo-400 text-[10px] font-bold tracking-widest uppercase">Coming Soon</span>
                  </span>
                )}
              </div>
              <span className="text-amber-400/40 text-[9px] font-bold tracking-[0.2em] uppercase">
                VIP Stage Pass
              </span>
            </div>

            {/* Guest/Celebrity = PRIMARY HEADLINE */}
            {event.guest_name ? (
              <>
                <h2
                  className="font-display text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1 leading-tight break-words"
                  data-testid="vip-guest-name"
                >
                  {event.guest_name}
                </h2>
                <p
                  className="text-amber-300/80 text-xs sm:text-sm font-semibold mb-3 break-words"
                  data-testid="vip-event-title"
                >
                  {event.title}
                </p>
              </>
            ) : (
              <h2
                className="font-display text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3 leading-tight break-words"
                data-testid="vip-event-title"
              >
                {event.title}
              </h2>
            )}

            {event.description && (
              <p className="text-white/40 text-xs sm:text-sm leading-relaxed mb-4 break-words" data-testid="vip-description">
                {event.description}
              </p>
            )}

            {/* Date/Time Row */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-white/30 text-[11px] sm:text-xs mb-4 sm:mb-5">
              {event.event_date && (
                <span>{formatDate(event.event_date)}</span>
              )}
              {event.event_date && (
                <span>{formatTime(event.event_date)}{event.event_end_date ? ` — ${formatTime(event.event_end_date)}` : ''}</span>
              )}
            </div>

            {/* Prominent Price + CTA */}
            {lowestPrice > 0 && (
              <div className="mb-4 sm:mb-5" data-testid="vip-price-prominent">
                <span className="text-white/30 text-[10px] sm:text-xs uppercase tracking-wider block mb-0.5">Starting at</span>
                <span className="text-amber-300 text-3xl sm:text-4xl md:text-5xl font-black tracking-tight leading-none">
                  {fmtPrice(lowestPrice)}
                </span>
              </div>
            )}

            {/* ── PRIMARY CTA ── */}
            {(isOnSale || isLive) && (
              <button
                onClick={(e) => { e.stopPropagation(); handleCTA(); }}
                className="group/cta flex items-center gap-2 sm:gap-3 px-5 sm:px-8 py-3 sm:py-3.5 rounded-xl bg-gradient-to-r from-amber-500 via-amber-500 to-amber-600 text-black text-xs sm:text-sm font-extrabold uppercase tracking-widest hover:from-amber-400 hover:to-amber-500 transition-all shadow-lg shadow-amber-500/25 hover:shadow-amber-400/40 hover:scale-[1.02] active:scale-[0.98] mb-4 sm:mb-5"
                data-testid="vip-get-pass-cta"
              >
                <Ticket size={18} className="transition-transform group-hover/cta:rotate-[-8deg]" />
                Get Pass
                <ChevronRight size={16} className="transition-transform group-hover/cta:translate-x-0.5" />
              </button>
            )}

            {/* Coming Soon state */}
            {isScheduled && (
              <div className="mb-5 px-5 py-3 rounded-xl border border-indigo-400/15 bg-indigo-400/[0.04] inline-flex items-center gap-2" data-testid="vip-coming-soon-msg">
                <Clock size={14} className="text-indigo-400/60" />
                <span className="text-indigo-300/60 text-xs font-bold uppercase tracking-wider">Tickets Available Soon</span>
              </div>
            )}

            {/* Sold Out state */}
            {isSoldOut && (
              <div className="mb-5 px-5 py-3 rounded-xl border border-white/10 bg-white/[0.02] inline-flex items-center gap-2" data-testid="vip-soldout-cta">
                <span className="text-white/30 text-xs font-bold uppercase tracking-wider">Sold Out</span>
              </div>
            )}

            {/* Ended state */}
            {isEnded && (
              <div className="mb-5 px-5 py-3 rounded-xl border border-white/5 bg-white/[0.01] inline-flex items-center gap-2">
                <span className="text-white/20 text-xs font-bold uppercase tracking-wider">Event Ended</span>
              </div>
            )}

            {/* Countdown */}
            {countdown && !countdown.expired && !isLive && (
              <div className="mb-4 sm:mb-6" data-testid="vip-countdown">
                <p className="text-white/20 text-[9px] sm:text-[10px] uppercase tracking-widest mb-2">Event Starts In</p>
                <div className="flex gap-2 sm:gap-3">
                  {countdown.d > 0 && <CountdownBlock value={countdown.d} label="Days" />}
                  <CountdownBlock value={countdown.h} label="Hours" />
                  <CountdownBlock value={countdown.m} label="Min" />
                  <CountdownBlock value={countdown.s} label="Sec" />
                </div>
              </div>
            )}

            {/* Inventory indicator */}
            {isOnSale && totalRemaining !== Infinity && totalRemaining > 0 && totalRemaining <= 20 && (
              <div className="flex items-center gap-1.5 mb-4" data-testid="vip-low-inventory">
                <AlertCircle size={12} className="text-amber-400" />
                <span className="text-amber-400 text-xs font-semibold">Only {totalRemaining} pass{totalRemaining !== 1 ? 'es' : ''} remaining</span>
              </div>
            )}
          </div>

          {/* RIGHT SIDE — Promotional Image (~40-45%) */}
          {event.image_url && (
            <div className="relative md:w-[42%] flex-shrink-0" data-testid="vip-promo-image-container">
              {/* Mobile: horizontal banner — fluid height */}
              <div className="block md:hidden w-full h-44 sm:h-56 relative">
                <img
                  src={event.image_url}
                  alt={event.guest_name || event.title}
                  className="w-full h-full object-cover"
                  data-testid="vip-event-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-transparent to-transparent" />
              </div>
              {/* Desktop/Tablet: right column, full height */}
              <div className="hidden md:block absolute inset-0">
                <img
                  src={event.image_url}
                  alt={event.guest_name || event.title}
                  className="w-full h-full object-cover"
                  data-testid="vip-event-image-desktop"
                />
                {/* Gradient fade into left content */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d0d] via-[#0d0d0d]/40 to-transparent" />
              </div>
            </div>
          )}
        </div>

        {/* Tier Selection Panel + Waitlist */}
        <div className="relative z-10 px-5 sm:px-6 md:px-8 pb-5 sm:pb-6 md:pb-8">
          <AnimatePresence>
            {showTiers && (isOnSale || isLive) && event.tiers?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
              >
                <div className="space-y-3 pb-2" data-testid="vip-tiers">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-white/40 text-[10px] font-bold uppercase tracking-widest">Select Your Pass</p>
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowTiers(false); }}
                      className="text-white/20 text-[10px] hover:text-white/40 transition-colors"
                      data-testid="vip-close-tiers"
                    >
                      Close
                    </button>
                  </div>
                  {event.tiers.map(tier => {
                    const soldOut = tier.total_inventory !== -1 && tier.remaining <= 0;
                    const isChecking = checkingOut === `${event.id}-${tier.tier_id}`;
                    return (
                      <div
                        key={tier.tier_id}
                        className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border transition-all ${
                          soldOut
                            ? 'bg-white/[0.02] border-white/5'
                            : 'bg-amber-400/[0.03] border-amber-400/10 hover:border-amber-400/25'
                        }`}
                        data-testid={`vip-tier-${tier.tier_id}`}
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-white text-sm font-bold">{tier.name}</span>
                            {tier.total_inventory !== -1 && !soldOut && (
                              <span className="text-white/20 text-[10px]">
                                {tier.remaining} left
                              </span>
                            )}
                          </div>
                          <span className="text-amber-300 text-base sm:text-lg font-bold">
                            {fmtPrice(tier.price_cents)}
                          </span>
                        </div>

                        {soldOut ? (
                          <span className="text-white/25 text-xs font-bold uppercase tracking-wider">Sold Out</span>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); onCheckout(event.id, tier.tier_id); }}
                            disabled={isChecking}
                            className="flex items-center justify-center gap-2 w-full sm:w-auto px-4 sm:px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-black text-xs font-bold uppercase tracking-wider hover:from-amber-400 hover:to-amber-500 transition-all disabled:opacity-50 shadow-lg shadow-amber-500/20"
                            data-testid={`vip-buy-${tier.tier_id}`}
                          >
                            {isChecking ? (
                              <span className="animate-pulse">Processing...</span>
                            ) : (
                              <>
                                Get Pass <ChevronRight size={14} />
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Sold Out + Waitlist */}
          {isSoldOut && event.waitlist_enabled && (
            <div className="mt-6" data-testid="vip-waitlist-section">
              {waitlistForm === event.id ? (
                <div className="space-y-3 p-4 rounded-xl border border-white/10 bg-white/[0.02]">
                  <p className="text-white/50 text-xs font-semibold">Join the Waitlist</p>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={waitlistData.name}
                    onChange={e => setWaitlistData(p => ({ ...p, name: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-amber-400/30"
                    data-testid="waitlist-name"
                  />
                  <input
                    type="text"
                    placeholder="Email or phone"
                    value={waitlistData.contact}
                    onChange={e => setWaitlistData(p => ({ ...p, contact: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-amber-400/30"
                    data-testid="waitlist-contact"
                  />
                  <button
                    onClick={() => onWaitlist(event.id)}
                    className="w-full py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-xs font-bold uppercase tracking-wider hover:bg-white/10 transition-colors"
                    data-testid="waitlist-submit"
                  >
                    Join Waitlist
                  </button>
                  {waitlistMsg && <p className="text-amber-400 text-xs text-center">{waitlistMsg}</p>}
                </div>
              ) : (
                <button
                  onClick={() => setWaitlistForm(event.id)}
                  className="w-full py-3 rounded-xl border border-white/10 text-white/40 text-xs font-bold uppercase tracking-wider hover:text-white/60 hover:border-white/20 transition-colors"
                  data-testid="waitlist-open"
                >
                  <Users size={14} className="inline mr-2" />
                  Join Waitlist — Get Notified If Spots Open
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default VIPMarquee;
